import { useState } from 'react';
import {
  AppButton,
  AppDateField,
  AppEmptyState,
  AppFormDialog,
  AppMenuSheet,
  AppScreenScaffold,
  AppSectionCard,
  AppSectionDivider,
  AppSectionRow,
  AppTextField,
  AppTopBar,
  AppUndoSnackbar,
  IconButton,
} from '@core/designsystem';
import { dayMonthTime } from '@core/ui/RuDates';
import type { TaskDetailActions, TaskDetailState } from './useTaskDetail';
import './tasks.css';

/**
 * Карточка задачи — «Структура» §6.2.
 *
 * Из блоков, как карточка дела ([D-36](docs/DECISIONS.md)): состояние и срок, «О задаче»,
 * дело. Всё правится на месте — задачу заводят в спешке, а дополняют потом.
 */
export function TaskDetailScreen({
  state,
  actions,
  onBack,
  onOpenCase,
  onOpenClient,
}: {
  state: TaskDetailState;
  actions: TaskDetailActions;
  onBack: () => void;
  onOpenCase: (caseId: string) => void;
  onOpenClient: (clientId: string) => void;
}) {
  const [editing, setEditing] = useState<'title' | 'description' | null>(null);
  const [movingDeadline, setMovingDeadline] = useState(false);
  const [pickingCase, setPickingCase] = useState(false);

  if (state.notFound) {
    return (
      <AppScreenScaffold topBar={<AppTopBar title="Задача" onBack={onBack} />}>
        <AppEmptyState icon="error" text="Задача не найдена" />
      </AppScreenScaffold>
    );
  }

  return (
    <AppScreenScaffold
      topBar={
        <AppTopBar
          title={state.title}
          subtitle={state.timing}
          onBack={onBack}
          actions={
            <IconButton
              icon="delete"
              label="Удалить"
              onClick={() => void actions.remove()}
            />
          }
        />
      }
      overlay={
        <>
          {editing ? (
            <TextDialog
              title={editing === 'title' ? 'Название' : 'Описание'}
              value={editing === 'title' ? state.title : state.description}
              onDismiss={() => setEditing(null)}
              onConfirm={(value) => {
                if (editing === 'title') actions.setTitle(value);
                else actions.setDescription(value);
                setEditing(null);
              }}
            />
          ) : null}

          {movingDeadline ? (
            <DeadlineDialog
              deadlineAt={state.deadlineAt}
              onDismiss={() => setMovingDeadline(false)}
              onConfirm={(at) => {
                actions.setDeadline(at);
                setMovingDeadline(false);
              }}
            />
          ) : null}

          {pickingCase ? (
            <AppMenuSheet
              title="Дело задачи"
              onDismiss={() => setPickingCase(false)}
              items={[
                { icon: 'close', label: 'Без дела', onSelect: () => actions.setCase(null) },
                ...state.cases.map((item) => ({
                  icon: 'folder' as const,
                  label: item.title,
                  onSelect: () => actions.setCase(item.id),
                })),
              ]}
            />
          ) : null}

          {state.isDeleted ? (
            <AppUndoSnackbar
              text="Задача удалена"
              onUndo={() => void actions.undoDelete()}
              onExpire={() => {
                actions.deletionShown();
                onBack();
              }}
            />
          ) : null}
        </>
      }
    >
      <AppSectionCard title="Состояние">
        <AppSectionRow
          icon={state.isCompleted ? 'check' : 'checklist'}
          title={state.isCompleted ? 'Выполнено' : 'В работе'}
          subtitle={state.timing}
          onClick={() => void actions.setCompleted(!state.isCompleted)}
        />
        <AppSectionDivider />
        <AppSectionRow
          icon="schedule"
          title={state.deadlineAt === null ? 'Срок не задан' : dayMonthTime(new Date(state.deadlineAt))}
          subtitle="Срок"
          onClick={() => setMovingDeadline(true)}
        />
      </AppSectionCard>

      <AppSectionCard title="О задаче">
        <AppSectionRow icon="edit" title={state.title} subtitle="Название" onClick={() => setEditing('title')} />
        <AppSectionDivider />
        {state.description ? (
          <AppSectionRow
            icon="description"
            title={state.description}
            subtitle="Описание"
            onClick={() => setEditing('description')}
          />
        ) : (
          <AppSectionRow
            icon="add"
            title="Добавить описание"
            action
            onClick={() => setEditing('description')}
          />
        )}
      </AppSectionCard>

      <AppSectionCard title="Дело">
        {state.caseId ? (
          <>
            <AppSectionRow
              icon="folder"
              title={state.caseTitle ?? 'Дело'}
              subtitle="Открыть дело"
              onClick={() => onOpenCase(state.caseId ?? '')}
            />
            <AppSectionDivider />
            <AppSectionRow icon="edit" title="Сменить дело" action onClick={() => setPickingCase(true)} />
            {/* Клиент дела: позвонить, не разыскивая его через дело. Своего клиента
                у задачи нет — она относится к делу, а дело к клиенту. */}
            {state.clientId ? (
              <>
                <AppSectionDivider />
                <AppSectionRow
                  icon="person"
                  title={state.clientName ?? ''}
                  subtitle="Клиент дела"
                  onClick={() => onOpenClient(state.clientId ?? '')}
                />
              </>
            ) : null}
          </>
        ) : (
          // Привязка предлагается, но не требуется: напоминание может не относиться
          // ни к какому делу — решение владельца 30.08.2026.
          <AppSectionRow icon="add" title="Привязать к делу" action onClick={() => setPickingCase(true)} />
        )}
      </AppSectionCard>
    </AppScreenScaffold>
  );
}

function TextDialog({
  title,
  value,
  onDismiss,
  onConfirm,
}: {
  title: string;
  value: string;
  onDismiss: () => void;
  onConfirm: (value: string) => void;
}) {
  const [text, setText] = useState(value);

  return (
    <AppFormDialog
      title={title}
      onDismiss={onDismiss}
      confirm={{ label: 'Готово', onClick: () => onConfirm(text) }}
    >
      <AppTextField value={text} onChange={setText} label={title} autoFocus />
    </AppFormDialog>
  );
}

function DeadlineDialog({
  deadlineAt,
  onDismiss,
  onConfirm,
}: {
  deadlineAt: number | null;
  onDismiss: () => void;
  onConfirm: (deadlineAt: number) => void;
}) {
  const current = new Date(deadlineAt ?? Date.now());
  const pad = (value: number) => String(value).padStart(2, '0');
  const [date, setDate] = useState(
    `${current.getFullYear()}-${pad(current.getMonth() + 1)}-${pad(current.getDate())}`,
  );
  const [clock, setClock] = useState(`${pad(current.getHours())}:${pad(current.getMinutes())}`);

  return (
    <AppFormDialog
      title="Срок задачи"
      onDismiss={onDismiss}
      confirm={{
        label: 'Готово',
        onClick: () => {
          const at = new Date(`${date}T${clock}`);
          if (!Number.isNaN(at.getTime())) onConfirm(at.getTime());
        },
      }}
    >
      <AppDateField value={date} onChange={setDate} label="Дата" />
      <AppTextField value={clock} onChange={setClock} label="Время" />
      <AppButton variant="text" onClick={() => setClock('12:00')}>
        Полдень
      </AppButton>
    </AppFormDialog>
  );
}
